import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { FaChevronLeft } from 'react-icons/fa';
import { jobPostingService, JobPosting, JobType } from '../services/jobPostingService';
import { jobApplicationService } from '../services/jobApplicationService';
import LoadingSpinner from '../components/LoadingSpinner';
import CareersHeader from '../components/CareersHeader';
import { useMobile } from '../hooks/useMobile';
import toast from 'react-hot-toast';

interface ApplicationFormData {
  mobileNumber: string;
  email: string;
  firstName: string;
  lastName: string;
  gender: string;
  dob: string;
}

interface DayAvailability {
  from: string;
  to: string;
}

interface AvailabilityData {
  allDays: boolean;
  selectedDays: Set<string>;
  dayHours: Record<string, DayAvailability>;
  allDaysHours: DayAvailability;
}

const JobApplication: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { isMobile } = useMobile();
  const [jobPosting, setJobPosting] = useState<JobPosting | null>(null);
  const [loading, setLoading] = useState(true);
  const [formData, setFormData] = useState<ApplicationFormData>({
    mobileNumber: '',
    email: '',
    firstName: '',
    lastName: '',
    gender: '',
    dob: '',
  });
  const [formErrors, setFormErrors] = useState<Partial<ApplicationFormData>>({});
  const [availability, setAvailability] = useState<AvailabilityData>({
    allDays: true,
    selectedDays: new Set<string>(),
    dayHours: {},
    allDaysHours: { from: '', to: '' },
  });
  const [availabilityError, setAvailabilityError] = useState<string>('');
  const [dayDropdownOpen, setDayDropdownOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement | null>(null);
  const [resumeFile, setResumeFile] = useState<File | null>(null);
  const [resumeError, setResumeError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const MAX_RESUME_SIZE_MB = 5;
  const MAX_RESUME_SIZE_BYTES = MAX_RESUME_SIZE_MB * 1024 * 1024;

  const WEEKDAYS = [
    { value: 'monday', label: 'Monday' },
    { value: 'tuesday', label: 'Tuesday' },
    { value: 'wednesday', label: 'Wednesday' },
    { value: 'thursday', label: 'Thursday' },
    { value: 'friday', label: 'Friday' },
    { value: 'saturday', label: 'Saturday' },
    { value: 'sunday', label: 'Sunday' },
  ];

  // Generate demo job postings (same as Careers page)
  const generateDemoJobPostings = (): JobPosting[] => {
    const now = new Date();
    return [
      {
        id: 'demo-001',
        jobId: 'CLBCO0001',
        jobTitle: 'Car Wash Attendant / Car Cleaner',
        jobType: JobType.FULL_TIME,
        position: 'Cleaner',
        jobDescriptionId: '',
        createdAt: new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000).toISOString(),
        updatedAt: new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000).toISOString(),
      },
      {
        id: 'demo-002',
        jobId: 'CLBCO0002',
        jobTitle: 'Detailer',
        jobType: JobType.FULL_TIME,
        position: 'Detailer',
        jobDescriptionId: '',
        createdAt: new Date(now.getTime() - 5 * 24 * 60 * 60 * 1000).toISOString(),
        updatedAt: new Date(now.getTime() - 5 * 24 * 60 * 60 * 1000).toISOString(),
      },
      {
        id: 'demo-003',
        jobId: 'CLBCO0003',
        jobTitle: 'Delivery Driver',
        jobType: JobType.FULL_TIME,
        position: 'Delivery Driver',
        jobDescriptionId: '',
        createdAt: new Date(now.getTime() - 3 * 24 * 60 * 60 * 1000).toISOString(),
        updatedAt: new Date(now.getTime() - 3 * 24 * 60 * 60 * 1000).toISOString(),
      },
    ];
  };

  useEffect(() => {
    loadJobPosting();
  }, [id]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dayDropdownOpen &&
        dropdownRef.current &&
        event.target instanceof Node &&
        !dropdownRef.current.contains(event.target)
      ) {
        setDayDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [dayDropdownOpen]);

  const loadJobPosting = async () => {
    if (!id) {
      navigate('/careers');
      return;
    }

    if (id.startsWith('demo-')) {
      const demoPostings = generateDemoJobPostings();
      const demoJob = demoPostings.find((p) => p.id === id);
      if (demoJob) {
        setJobPosting(demoJob);
        setLoading(false);
        return;
      }

      navigate('/careers');
      return;
    }

    try {
      setLoading(true);
      const posting = await jobPostingService.getJobPosting(id);
      setJobPosting(posting);
    } catch (error) {
      console.error('Error loading job posting:', error);
      navigate('/careers');
    } finally {
      setLoading(false);
    }
  };

  const validateForm = (): boolean => {
    const errors: Partial<ApplicationFormData> = {};

    // Remove formatting from mobile number for validation
    const mobileNumberDigits = formData.mobileNumber.replace(/\D/g, '');
    
    if (!mobileNumberDigits) {
      errors.mobileNumber = 'Mobile number is required';
    } else if (mobileNumberDigits.length !== 10) {
      errors.mobileNumber = 'Please enter a valid 10-digit mobile number';
    }

    // Email is optional, but if provided, it must be valid
    if (formData.email.trim() && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      errors.email = 'Please enter a valid email address';
    }

    if (!formData.firstName.trim()) {
      errors.firstName = 'First name is required';
    }

    if (!formData.lastName.trim()) {
      errors.lastName = 'Last name is required';
    }

    if (!formData.gender) {
      errors.gender = 'Gender is required';
    }

    if (!formData.dob) {
      errors.dob = 'Date of birth is required';
    } else {
      const dobDate = new Date(formData.dob);
      const today = new Date();
      if (dobDate >= today) {
        errors.dob = 'Date of birth must be in the past';
      }
    }

    setFormErrors(errors);

    let availabilityErr = '';
    if (availability.allDays) {
      if (!availability.allDaysHours.from || !availability.allDaysHours.to) {
        availabilityErr = 'Please provide hours for all days';
      } else if (availability.allDaysHours.from >= availability.allDaysHours.to) {
        availabilityErr = 'End time must be after start time';
      }
    } else if (availability.selectedDays.size === 0) {
      availabilityErr = 'Please select at least one day or choose "All Days"';
    } else {
      for (const day of availability.selectedDays) {
        const hours = availability.dayHours[day];
        if (!hours || !hours.from || !hours.to) {
          availabilityErr = `Please provide hours for ${WEEKDAYS.find((d) => d.value === day)?.label || day}`;
          break;
        }
        if (hours.from >= hours.to) {
          availabilityErr = `End time must be after start time for ${WEEKDAYS.find((d) => d.value === day)?.label || day}`;
          break;
        }
      }
    }
    setAvailabilityError(availabilityErr);

    let resumeValidationError = '';
    if (!resumeFile) {
      resumeValidationError = 'Please upload your resume (PDF, max 5MB).';
    } else if (
      resumeFile.type !== 'application/pdf' &&
      !resumeFile.name.toLowerCase().endsWith('.pdf')
    ) {
      resumeValidationError = 'Only PDF files are allowed.';
    } else if (resumeFile.size > MAX_RESUME_SIZE_BYTES) {
      resumeValidationError = `File size must be less than ${MAX_RESUME_SIZE_MB}MB.`;
    }
    setResumeError(resumeValidationError);

    return Object.keys(errors).length === 0 && !availabilityErr && !resumeValidationError;
  };

  // Format US phone number as XXX-XXX-XXXX
  const formatPhoneNumber = (value: string): string => {
    // Remove all non-digit characters
    const phoneNumber = value.replace(/\D/g, '');
    
    // Limit to 10 digits
    const phoneNumberDigits = phoneNumber.slice(0, 10);
    
    // Format based on length
    if (phoneNumberDigits.length === 0) {
      return '';
    } else if (phoneNumberDigits.length <= 3) {
      return phoneNumberDigits;
    } else if (phoneNumberDigits.length <= 6) {
      return `${phoneNumberDigits.slice(0, 3)}-${phoneNumberDigits.slice(3)}`;
    } else {
      return `${phoneNumberDigits.slice(0, 3)}-${phoneNumberDigits.slice(3, 6)}-${phoneNumberDigits.slice(6)}`;
    }
  };

  const handleMobileNumberChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const formattedValue = formatPhoneNumber(e.target.value);
    setFormData((prev) => ({
      ...prev,
      mobileNumber: formattedValue,
    }));

    if (formErrors.mobileNumber) {
      setFormErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors.mobileNumber;
        return newErrors;
      });
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    
    // Skip mobile number as it has its own handler
    if (name === 'mobileNumber') {
      return;
    }
    
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));

    if (formErrors[name as keyof ApplicationFormData]) {
      setFormErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors[name as keyof ApplicationFormData];
        return newErrors;
      });
    }
  };

  const handleAllDaysHoursChange = (field: 'from' | 'to', value: string) => {
    setAvailability((prev) => ({
      ...prev,
      allDaysHours: {
        ...prev.allDaysHours,
        [field]: value,
      },
    }));
    setAvailabilityError('');
  };

  const handleDayToggle = (day: string) => {
    const newSelectedDays = new Set(availability.selectedDays);
    if (newSelectedDays.has(day)) {
      newSelectedDays.delete(day);
      const newDayHours = { ...availability.dayHours };
      delete newDayHours[day];
      setAvailability({
        ...availability,
        selectedDays: newSelectedDays,
        dayHours: newDayHours,
      });
    } else {
      newSelectedDays.add(day);
      setAvailability({
        ...availability,
        selectedDays: newSelectedDays,
        dayHours: {
          ...availability.dayHours,
          [day]: { from: '', to: '' },
        },
      });
    }
    setAvailabilityError('');
  };

  const handleDayHoursChange = (day: string, field: 'from' | 'to', value: string) => {
    setAvailability((prev) => ({
      ...prev,
      dayHours: {
        ...prev.dayHours,
        [day]: {
          ...prev.dayHours[day],
          [field]: value,
        },
      },
    }));
    setAvailabilityError('');
  };

  const handleResumeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) {
      setResumeFile(null);
      setResumeError('Please upload your resume (PDF, max 5MB).');
      return;
    }

    if (file.type !== 'application/pdf' && !file.name.toLowerCase().endsWith('.pdf')) {
      setResumeFile(null);
      setResumeError('Only PDF files are allowed.');
      return;
    }

    if (file.size > MAX_RESUME_SIZE_BYTES) {
      setResumeFile(null);
      setResumeError(`File size must be less than ${MAX_RESUME_SIZE_MB}MB.`);
      return;
    }

    setResumeFile(file);
    setResumeError('');
  };

  const handleRemoveResume = () => {
    setResumeFile(null);
    setResumeError('');
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validateForm() || !jobPosting) {
      return;
    }

    try {
      setSubmitting(true);

      const applicationData = {
        jobPostingId: jobPosting.id,
        mobileNumber: formData.mobileNumber.replace(/\D/g, ''), // Remove formatting
        email: formData.email || undefined,
        firstName: formData.firstName,
        lastName: formData.lastName,
        gender: formData.gender,
        dob: formData.dob,
        resumeFileName: resumeFile?.name,
        availability: availability.allDays
          ? {
              allDays: true,
              hours: availability.allDaysHours,
            }
          : {
              allDays: false,
              days: Array.from(availability.selectedDays).map((day) => ({
                day,
                hours: availability.dayHours[day],
              })),
            },
      };

      await jobApplicationService.createApplication(applicationData, resumeFile || undefined);
      
      toast.success('Application submitted successfully!');
      navigate(jobPosting ? `/job/${jobPosting.id}` : '/careers');
    } catch (error: any) {
      console.error('Error submitting application:', error);
      toast.error(error.response?.data?.message || error.message || 'Failed to submit application. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <LoadingSpinner />
      </div>
    );
  }

  if (!jobPosting) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">Job Posting Not Found</h2>
          <Link to="/careers" className="text-blue-600 hover:underline">
            Back to Careers
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <CareersHeader />

      <div className="pt-28">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <button
            onClick={() => navigate(`/job/${jobPosting.id}`)}
            className="flex items-center space-x-2 text-sm text-gray-600 hover:text-gray-800 mb-4 transition-colors"
          >
            <FaChevronLeft className="w-4 h-4" />
            <span>Back to Job Details</span>
          </button>

          <div className="p-4 rounded-lg">
                <h2 className="text-lg sm:text-xl font-semibold text-gray-900">
                  Complete your application
                </h2>
                <p className="text-sm text-gray-700 mt-1">
                  Share your details below so our hiring team can get to know you better.
                </p>
              </div>
          <div className="bg-white rounded-lg shadow-md p-6 sm:p-8">

            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Sign In Details */}
              <section>
                <h3 className="text-lg font-semibold text-gray-900 mb-4 pb-2 border-b border-gray-200">
                  Sign In Details
                </h3>
                <div className="space-y-4">
                  <div>
                    <label htmlFor="mobileNumber" className="block text-sm font-medium text-gray-700 mb-1">
                      Mobile Number <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="tel"
                      id="mobileNumber"
                      name="mobileNumber"
                      value={formData.mobileNumber}
                      onChange={handleMobileNumberChange}
                      className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                        formErrors.mobileNumber ? 'border-red-500' : 'border-gray-300'
                      }`}
                      placeholder="XXX-XXX-XXXX"
                      maxLength={12}
                    />
                    {formErrors.mobileNumber && (
                      <p className="mt-1 text-sm text-red-500">{formErrors.mobileNumber}</p>
                    )}
                  </div>

                  <div>
                    <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">
                      Email
                    </label>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      value={formData.email}
                      onChange={handleInputChange}
                      className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                        formErrors.email ? 'border-red-500' : 'border-gray-300'
                      }`}
                      placeholder="Enter your email address (optional)"
                    />
                    {formErrors.email && <p className="mt-1 text-sm text-red-500">{formErrors.email}</p>}
                  </div>
                </div>
              </section>

              {/* Personal Details */}
              <section>
                <h3 className="text-lg font-semibold text-gray-900 mb-4 pb-2 border-b border-gray-200">
                  Personal Details
                </h3>
                <div className="space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label htmlFor="firstName" className="block text-sm font-medium text-gray-700 mb-1">
                        First Name <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="text"
                        id="firstName"
                        name="firstName"
                        value={formData.firstName}
                        onChange={handleInputChange}
                        className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                          formErrors.firstName ? 'border-red-500' : 'border-gray-300'
                        }`}
                        placeholder="Enter your first name"
                      />
                      {formErrors.firstName && <p className="mt-1 text-sm text-red-500">{formErrors.firstName}</p>}
                    </div>

                    <div>
                      <label htmlFor="lastName" className="block text-sm font-medium text-gray-700 mb-1">
                        Last Name <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="text"
                        id="lastName"
                        name="lastName"
                        value={formData.lastName}
                        onChange={handleInputChange}
                        className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                          formErrors.lastName ? 'border-red-500' : 'border-gray-300'
                        }`}
                        placeholder="Enter your last name"
                      />
                      {formErrors.lastName && <p className="mt-1 text-sm text-red-500">{formErrors.lastName}</p>}
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label htmlFor="dob" className="block text-sm font-medium text-gray-700 mb-1">
                        Date of Birth <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="date"
                        id="dob"
                        name="dob"
                        value={formData.dob}
                        onChange={handleInputChange}
                        className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                          formErrors.dob ? 'border-red-500' : 'border-gray-300'
                        }`}
                        max={new Date().toISOString().split('T')[0]}
                      />
                      {formErrors.dob && <p className="mt-1 text-sm text-red-500">{formErrors.dob}</p>}
                    </div>

                    <div>
                      <label htmlFor="gender" className="block text-sm font-medium text-gray-700 mb-1">
                        Gender <span className="text-red-500">*</span>
                      </label>
                      <select
                        id="gender"
                        name="gender"
                        value={formData.gender}
                        onChange={handleInputChange}
                        className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                          formErrors.gender ? 'border-red-500' : 'border-gray-300'
                        }`}
                      >
                        <option value="">Select gender</option>
                        <option value="male">Male</option>
                        <option value="female">Female</option>
                        <option value="prefer-not-to-say">Prefer not to say</option>
                      </select>
                      {formErrors.gender && <p className="mt-1 text-sm text-red-500">{formErrors.gender}</p>}
                    </div>
                  </div>
                </div>
              </section>

              {/* Availability */}
              <section>
                <h3 className="text-lg font-semibold text-gray-900 mb-4 pb-2 border-b border-gray-200">
                  Availability
                </h3>
                <div className="space-y-6">
                  <div className="space-y-3">
                    <div className="flex items-center space-x-4">
                      <label className="flex items-center cursor-pointer">
                        <input
                          type="radio"
                          name="availabilityType"
                          checked={availability.allDays}
                          onChange={() => {
                            setAvailability({
                              ...availability,
                              allDays: true,
                              selectedDays: new Set<string>(),
                              dayHours: {},
                            });
                            setAvailabilityError('');
                          }}
                          className="w-4 h-4 text-blue-600 border-gray-300 focus:ring-blue-500"
                        />
                        <span className="ml-2 text-sm font-medium text-gray-700">All Days</span>
                      </label>
                      <label className="flex items-center cursor-pointer">
                        <input
                          type="radio"
                          name="availabilityType"
                          checked={!availability.allDays}
                          onChange={() => {
                            setAvailability({
                              ...availability,
                              allDays: false,
                            });
                            setAvailabilityError('');
                          }}
                          className="w-4 h-4 text-blue-600 border-gray-300 focus:ring-blue-500"
                        />
                        <span className="ml-2 text-sm font-medium text-gray-700">Select Specific Days</span>
                      </label>
                    </div>
                  </div>

                  {availability.allDays && (
                    <div className="bg-white border border-gray-200 rounded-lg p-4">
                      <label className="block text-sm font-medium text-gray-700 mb-3">
                        Working Hours (Applies to all days)
                      </label>
                      <div className="flex flex-row flex-wrap items-center gap-3">
                        <div className="flex-1">
                          <label htmlFor="all-days-from" className="block text-xs text-gray-600 mb-1">
                            From
                          </label>
                          <input
                            type="time"
                            id="all-days-from"
                            value={availability.allDaysHours.from}
                            onChange={(e) => handleAllDaysHoursChange('from', e.target.value)}
                            className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white"
                          />
                        </div>
                       
                        <div className="flex-1">
                          <label htmlFor="all-days-to" className="block text-xs text-gray-600 mb-1">
                            To
                          </label>
                          <input
                            type="time"
                            id="all-days-to"
                            value={availability.allDaysHours.to}
                            onChange={(e) => handleAllDaysHoursChange('to', e.target.value)}
                            className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white"
                          />
                        </div>
                      </div>
                    </div>
                  )}

                  {!availability.allDays && (
                    <div className="space-y-4">
                      
                      <div className="relative" ref={dropdownRef}>
                        <button
                          type="button"
                          onClick={() => setDayDropdownOpen((open) => !open)}
                          className="w-full flex items-center justify-between px-4 py-2 border border-gray-300 rounded-md bg-white text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                        >
                          <span className="truncate text-left">
                            {availability.selectedDays.size === 0
                              ? 'Select days'
                              : Array.from(availability.selectedDays)
                                  .map((day) => WEEKDAYS.find((d) => d.value === day)?.label || day)
                                  .join(', ')}
                          </span>
                          <svg
                            className={`w-4 h-4 text-gray-500 transform transition-transform ${
                              dayDropdownOpen ? 'rotate-180' : ''
                            }`}
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                          </svg>
                        </button>

                        {dayDropdownOpen && (
                          <div className="absolute z-10 mt-2 w-full bg-white border border-gray-200 rounded-lg shadow-lg max-h-56 overflow-y-auto">
                            {WEEKDAYS.map((weekday) => {
                              const isSelected = availability.selectedDays.has(weekday.value);
                              return (
                                <label
                                  key={weekday.value}
                                  className="flex items-center px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 cursor-pointer"
                                >
                                  <input
                                    type="checkbox"
                                    checked={isSelected}
                                    onChange={() => handleDayToggle(weekday.value)}
                                    className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                                  />
                                  <span className="ml-3">{weekday.label}</span>
                                </label>
                              );
                            })}
                          </div>
                        )}
                      </div>

                      {availability.selectedDays.size > 0 && (
                        <div className="space-y-3">
                          {Array.from(availability.selectedDays).map((day) => {
                            const hours = availability.dayHours[day] || { from: '', to: '' };
                            const label = WEEKDAYS.find((d) => d.value === day)?.label || day;
                            return (
                              <div key={day} className="border border-gray-200 rounded-lg p-3 bg-white">
                                <div className="flex items-center justify-between mb-3">
                                  <p className="text-sm font-medium text-gray-900">Working Hours for {label}</p>
                                  <button
                                    type="button"
                                    onClick={() => handleDayToggle(day)}
                                    className="text-xs text-red-600 hover:text-red-700"
                                  >
                                    Remove
                                  </button>
                                </div>
                                <div className="flex flex-row flex-wrap items-center gap-3">
                                  <div className="flex-1">
                                    <label className="block text-xs text-gray-600 mb-1">From</label>
                                    <input
                                      type="time"
                                      value={hours.from}
                                      onChange={(e) => handleDayHoursChange(day, 'from', e.target.value)}
                                      className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white"
                                    />
                                  </div>
                                  <div className="pt-1 text-center">
                                    <span className="text-gray-500 font-medium">to</span>
                                  </div>
                                  <div className="flex-1">
                                    <label className="block text-xs text-gray-600 mb-1">To</label>
                                    <input
                                      type="time"
                                      value={hours.to}
                                      onChange={(e) => handleDayHoursChange(day, 'to', e.target.value)}
                                      className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white"
                                    />
                                  </div>
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  )}

                  {availabilityError && (
                    <div className="bg-red-50 border border-red-200 rounded-md p-3">
                      <p className="text-sm text-red-600">{availabilityError}</p>
                    </div>
                  )}
                </div>
              </section>

              {/* Document Upload */}
              <section>
                <h3 className="text-lg font-semibold text-gray-900 mb-4 pb-2 border-b border-gray-200">
                  Upload Resume
                </h3>
                <div className="space-y-3">
                  <div className="flex flex-col sm:flex-row sm:items-center sm:space-x-3 space-y-3 sm:space-y-0">
                    <input
                      type="file"
                      accept="application/pdf"
                      onChange={handleResumeChange}
                      className="block w-full text-sm text-gray-700 file:mr-4 file:py-2 file:px-4 file:rounded-md file:border-0 file:text-sm file:font-semibold file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100 cursor-pointer border border-dashed border-gray-300 rounded-md p-2 bg-white"
                    />
                    {resumeFile && (
                      <button
                        type="button"
                        onClick={handleRemoveResume}
                        className="px-3 py-2 text-sm text-red-600 hover:text-red-700"
                      >
                        Remove
                      </button>
                    )}
                  </div>
                  {resumeError && <p className="text-sm text-red-500">{resumeError}</p>}
                  {!resumeFile && !resumeError && (
                    <p className="text-xs text-gray-500">
                      Accepted format: PDF only. Maximum file size {MAX_RESUME_SIZE_MB} MB.
                    </p>
                  )}
                </div>
              </section>

              <div className="flex flex-col sm:flex-row sm:justify-end sm:space-x-3 space-y-3 sm:space-y-0">
                <button
                  type="button"
                  onClick={() => navigate(`/job/${jobPosting.id}`)}
                  className="px-4 py-2 text-gray-700 bg-gray-200 rounded-md hover:bg-gray-300 transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={submitting}
                  className={`px-6 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors font-semibold disabled:opacity-50 disabled:cursor-not-allowed ${
                    isMobile ? 'w-full' : ''
                  }`}
                >
                  {submitting ? 'Submitting...' : 'Submit Application'}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
};

export default JobApplication;


